import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from '../../store/hooks';
import { useAuth } from '../../hooks/useAuth';
import AuthLayout from '../common/AuthLayout';
import FeatureList from '../common/FeatureList';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import EmailVerificationScreen from './EmailVerificationScreen';
import { auth0Config } from '../../config/auth0Config';

const features = [
  {
    icon: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    ),
    title: 'Track Your Compliance',
    description:
      'Monitor your compliance status and requirements in real-time',
  },
  {
    icon: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
    title: 'Complete Training',
    description:
      'Access required training modules and certifications',
  },
  {
    icon: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: 'Stay Compliant',
    description:
      'Get notified about upcoming deadlines and requirements',
  },
];

function SigninScreen() {
  const { loginWithRedirect, isLoading: auth0Loading } = useAuth0();
  const authState = useAppSelector((state) => state.auth);
  const { showEmailVerification } = useAuth();
  const { isLoading: contextLoading, backendSignInError } = authState;

  const handleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        audience: auth0Config.authorizationParams.audience,
        scope: 'openid profile email offline_access',
      },
    });
  };

  if (showEmailVerification) {
    return <EmailVerificationScreen />;
  }

  const isLoading = auth0Loading || contextLoading;

  const leftContent = (
    <>
      <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
        Welcome to Compliance Hub 👋
      </h1>
      <p className="text-lg md:text-xl mb-8 text-gray-700">
        Your employee portal to ensure compliance. Access training,
        certifications, and track your compliance status all in one place.
      </p>
      <FeatureList features={features} />
    </>
  );

  const rightContent = (
    <>
      <div className="space-y-6">
        {backendSignInError && (
          <Alert type="error">{backendSignInError}</Alert>
        )}

        <Button
          variant="primary"
          size="lg"
          onClick={handleLogin}
          isLoading={isLoading}
          className="w-full"
        >
          Sign in to Compliance Hub
        </Button>
      </div>
    </>
  );

  return (
    <AuthLayout
      leftContent={leftContent}
      rightContent={rightContent}
      gradientFrom="from-blue-50"
      gradientVia="via-sky-50"
      gradientTo="to-blue-100"
    />
  );
}

export default SigninScreen;

